import { Box, Stack, Switch, Typography } from '@mui/material';
import {
  AccessConditionEffect,
  ConditionOperatorEnum,
  type RestoreDestinationLimits,
} from '@repo/api-gw-sdk';
import { useRef } from 'react';

import { AccountCondition } from '@/components/accounts/accountCondition';
import { useDAL } from '@/contexts/useDAL';
import { PropertyType, type FilterProperty } from '@/types/advanceFilter';

import { InclusiveOptionsMenu } from './inclusiveOptionsMenu';
import { RestoreDestinationsIcon } from './restoreDestinationsIcon';

const restoreAccountProperty: FilterProperty = {
  name: 'restoreAccountId',
  displayName: 'Restore account ID',
  type: PropertyType.SingleValue,
  group: '',
};

export const RestoreAccountLimitsEditor = ({
  restoreDestinationLimits,
  onChange,
}: {
  restoreDestinationLimits: RestoreDestinationLimits | null | undefined;
  onChange: (value: RestoreDestinationLimits | null | undefined) => void;
}) => {
  const dal = useDAL();
  const { data: restoreAccountsBody } = dal.cloudAccounts.restore.useList();
  const restoreAccounts = restoreAccountsBody?.accounts || [];

  const previousConditionRef = useRef<RestoreDestinationLimits | null>(null);

  const isEnabled = !!restoreDestinationLimits;
  const isInclusive =
    restoreDestinationLimits?.effect ===
    AccessConditionEffect.RULE_EFFECT_INCLUSIVE;
  const selectedIdentifiers =
    restoreDestinationLimits?.restoreAccountProviderIds || [];

  if (restoreDestinationLimits) {
    previousConditionRef.current = restoreDestinationLimits;
  }

  return (
    <Stack gap='16px'>
      <Stack direction='row' alignItems='center' className='py-[8px]'>
        <Switch
          checked={isEnabled}
          onChange={(e) => {
            if (e.target.checked) {
              onChange(
                previousConditionRef.current || {
                  effect: AccessConditionEffect.RULE_EFFECT_EXCLUSIVE,
                  restoreAccountProviderIds: [],
                }
              );
            } else {
              onChange(null);
            }
          }}
        />
        <Box
          component='span'
          sx={{
            ml: '24px',
            mr: '12px',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <RestoreDestinationsIcon />
        </Box>
        <Stack direction='row' alignItems='center' gap='8px'>
          <InclusiveOptionsMenu
            isInclusive={isInclusive}
            setInclusive={(value) =>
              onChange({
                ...restoreDestinationLimits!,
                effect: value
                  ? AccessConditionEffect.RULE_EFFECT_INCLUSIVE
                  : AccessConditionEffect.RULE_EFFECT_EXCLUSIVE,
              })
            }
            disabled={!isEnabled}
          />
          <Typography>
            restore to accounts that meet these conditions:
          </Typography>
        </Stack>
      </Stack>
      {isEnabled && (
        <Stack
          direction='row'
          alignItems='center'
          gap='12px'
          className='ml-[62px]'
          flexWrap='wrap'
        >
          <Typography variant='body2'>
            Restore account ID is one of...
          </Typography>
          <AccountCondition
            accounts={restoreAccounts}
            property={restoreAccountProperty}
            entityName='restore account id'
            isLoading={!restoreAccountsBody}
            showAddNewButton
            showSearchBox
            shouldShowId
            condition={{
              property: 'restoreAccountId',
              operator: ConditionOperatorEnum.In,
              value: selectedIdentifiers,
            }}
            onChange={(condition) => {
              const value = 'value' in condition ? condition.value! : [];
              onChange({
                ...restoreDestinationLimits,
                restoreAccountProviderIds: value,
              });
            }}
          />
        </Stack>
      )}
    </Stack>
  );
};
